import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from '@/hooks/auth';
import { supabase } from '@/integrations/supabase/client';

export type OnboardingStatus = 'pending' | 'in_progress' | 'completed';
export type OnboardingStep = 1 | 2 | 3 | 4 | 5;

interface OnboardingContextType {
  status: OnboardingStatus | null;
  currentStep: OnboardingStep;
  isLoading: boolean;
  error: Error | null;
  isPrivilegedRole: boolean;
  updateOnboardingStatus: (status: OnboardingStatus, step?: OnboardingStep) => Promise<void>;
  completeOnboarding: () => Promise<void>;
  skipOnboarding: () => Promise<void>;
  refreshOnboardingStatus: () => Promise<void>;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (context === undefined) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
};

interface OnboardingProviderProps {
  children: ReactNode;
}

export const OnboardingProvider: React.FC<OnboardingProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [status, setStatus] = useState<OnboardingStatus | null>(null);
  const [currentStep, setCurrentStep] = useState<OnboardingStep>(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [isPrivilegedRole, setIsPrivilegedRole] = useState(false);

  const loadOnboardingStatus = async () => {
    if (!user?.id) {
      setIsLoading(false);
      setStatus(null);
      setError(null);
      console.log('[OnboardingContext] No user ID, skipping load.');
      return;
    }

    try {
      setError(null);
      const { data, error } = await supabase
        .from('profiles')
        .select('onboarding_status, onboarding_step, is_recruiter, is_admin, is_superadmin')
        .eq('id', user.id)
        .single();

      console.log('[OnboardingContext] Fetched onboarding status:', { data, error });

      if (error) throw error;

      const profileData = data as any;
      const privileged = !!(profileData?.is_recruiter || profileData?.is_admin || profileData?.is_superadmin);
      setIsPrivilegedRole(privileged);
      setStatus(profileData?.onboarding_status ?? null);
      setCurrentStep((profileData?.onboarding_step as OnboardingStep) || 1);
      console.log('[OnboardingContext] Set status:', profileData?.onboarding_status || 'pending', 'Set step:', profileData?.onboarding_step || 1, 'Privileged:', privileged);
    } catch (err) {
      console.error('[OnboardingContext] Error loading onboarding status:', err);
      setError(err as Error);
      setStatus(null);
    } finally {
      setIsLoading(false);
    }
  };

  const updateOnboardingStatus = async (
    newStatus: OnboardingStatus, 
    step?: OnboardingStep
  ) => {
    if (!user?.id) return;
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          onboarding_status: newStatus,
          onboarding_step: step || currentStep
        })
        .eq('id', user.id);
      if (error) throw error;
      setStatus(newStatus);
      if (step) setCurrentStep(step);
    } catch (error) {
      console.error('Error updating onboarding status:', error);
      throw error;
    }
  };

  const completeOnboarding = async () => {
    await updateOnboardingStatus('completed', 5);
  };

  const skipOnboarding = async () => {
    await updateOnboardingStatus('completed', 5);
  };

  const refreshOnboardingStatus = async () => {
    await loadOnboardingStatus();
  };

  useEffect(() => {
    loadOnboardingStatus();
  }, [user?.id]);

  const value: OnboardingContextType = {
    status,
    currentStep,
    isLoading,
    error,
    isPrivilegedRole,
    updateOnboardingStatus,
    completeOnboarding,
    skipOnboarding,
    refreshOnboardingStatus,
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
}; 